import React, { useState, useEffect } from 'react';
import { Search, Filter, CheckCircle, Loader2, Clock, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResolveReport = () => {
  const [resolvedTickets, setResolvedTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, avg_resolution_hours: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchResolvedTickets();
  }, []);

  const fetchResolvedTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tickets/resolved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setResolvedTickets(response.data.tickets || []);
        setStats(response.data.stats || { total: 0, today: 0, avg_resolution_hours: 0 });
      }
    } catch (error) {
      console.error('Error fetching resolved tickets:', error);
      toast.error('Failed to fetch resolved tickets');
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = resolvedTickets.filter(ticket => {
    const search = searchTerm.toLowerCase();
    return (
      (ticket.ticket_number?.toLowerCase() || '').includes(search) ||
      (ticket.title?.toLowerCase() || '').includes(search) ||
      (ticket.category?.toLowerCase() || '').includes(search)
    );
  });

  // Bug fix: removed shadowing formatDate – using imported one

  const calculateResolutionTime = (createdAt, resolvedAt) => {
    if (!createdAt || !resolvedAt) return '-';
    try {
      const created = new Date(createdAt);
      const resolved = new Date(resolvedAt);
      const hours = Math.round((resolved - created) / (1000 * 60 * 60));
      if (hours < 24) return `${hours}h`;
      const days = Math.round(hours / 24);
      return `${days}d`;
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-6" data-testid="resolve-report-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Resolve Report</h1>
        <p className="text-sm text-gray-500 mt-1">Track resolved tickets and resolutions</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Resolved Today</p>
              <p className="text-2xl font-bold text-green-600">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.today}
              </p>
            </div>
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Resolved</p>
              <p className="text-2xl font-bold text-blue-600">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.total}
              </p>
            </div>
            <Calendar className="h-10 w-10 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Resolution Time</p>
              <p className="text-2xl font-bold text-gray-900">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : `${stats.avg_resolution_hours}h`}
              </p>
            </div>
            <Clock className="h-10 w-10 text-gray-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search resolved tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="search-input"
              />
            </div>
            <button 
              onClick={fetchResolvedTickets}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolution Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolved Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      No resolved tickets to display.
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {ticket.ticket_number || ticket.id?.substring(0, 8).toUpperCase()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{ticket.title || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                          {ticket.category || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          ticket.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                          ticket.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {ticket.priority || 'medium'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {calculateResolutionTime(ticket.created_at, ticket.resolved_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(ticket.resolved_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResolveReport;
